import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import {
  UserIcon, DevicePhoneMobileIcon, MapPinIcon, EnvelopeIcon,
  ShoppingBagIcon, ArrowLeftIcon, PencilIcon, ArrowRightOnRectangleIcon,
  CheckCircleIcon, ClockIcon, TruckIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STATUS = {
  confirmed:  { bg:'#ecfdf5', color:'#059669', label:'Confirmed'  },
  completed:  { bg:'#dbeafe', color:'#2563eb', label:'Completed'  },
  dispatched: { bg:'#fef3c7', color:'#d97706', label:'Dispatched' },
  cancelled:  { bg:'#fee2e2', color:'#dc2626', label:'Cancelled'  },
  pending:    { bg:'#f3f4f6', color:'#6b7280', label:'Pending'    },
};

const Spin = () => (
  <span style={{display:'inline-block',width:'.9rem',height:'.9rem',
    border:'2px solid rgba(255,255,255,.35)',borderTopColor:'white',
    borderRadius:'50%',animation:'cps .7s linear infinite',flexShrink:0}}/>
);

const CustomerProfile = ({ onClose }) => {
  const navigate = useNavigate();
  const { customer, fetchProfile, updateProfile, logout, isLoggedIn } = useCustomerAuth();
  const [profile, setProfile] = useState(null);
  const [orders,  setOrders]  = useState([]);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ name:'', email:'', address:'' });
  const [saving,  setSaving]  = useState(false);
  const [tab,     setTab]     = useState('profile'); // 'profile' | 'orders'

  useEffect(() => {
    if (!isLoggedIn) return;
    loadProfile();
  }, [isLoggedIn]);

  const loadProfile = async () => {
    try {
      const res = await fetchProfile();
      if (res.success) {
        setProfile(res.customer);
        setOrders(res.orders || []);
        setForm({ name: res.customer.name||'', email: res.customer.email||'', address: res.customer.address||'' });
      }
    } catch { toast.error('Failed to load profile'); }
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await updateProfile(form);
      if (res.success) { toast.success('Profile updated! ✅'); setEditing(false); loadProfile(); }
      else toast.error(res.message || 'Update failed');
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out 👋');
    if (onClose) onClose();
    else navigate('/');
  };

  if (!isLoggedIn || !profile) return null;

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    @keyframes cps{to{transform:rotate(360deg)}}
    @keyframes cp-up{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    .cp-ov{position:fixed;inset:0;background:rgba(0,0,0,.5);backdrop-filter:blur(5px);
           z-index:450;display:flex;align-items:flex-end;justify-content:center;padding:0;}
    @media(min-width:640px){.cp-ov{align-items:center;padding:1rem;}}
    .cp-bx{background:#f0fdf4;width:100%;max-width:480px;border-radius:2rem 2rem 0 0;
           max-height:92vh;overflow-y:auto;animation:cp-up .35s ease-out;
           box-shadow:0 -20px 60px rgba(0,0,0,.18);}
    @media(min-width:640px){.cp-bx{border-radius:2rem;max-height:88vh;}}
    .cp-hdr{background:linear-gradient(135deg,#059669,#0891b2);padding:1.5rem 1.25rem 1.25rem;
            border-radius:2rem 2rem 0 0;position:relative;}
    @media(min-width:640px){.cp-hdr{border-radius:2rem 2rem 0 0;}}
    .cp-cl{position:absolute;top:1rem;right:1rem;background:rgba(255,255,255,.2);border:none;
           border-radius:50%;width:2rem;height:2rem;cursor:pointer;color:white;
           display:flex;align-items:center;justify-content:center;}
    .cp-av{width:3.5rem;height:3.5rem;background:rgba(255,255,255,.25);
           border:2.5px solid rgba(255,255,255,.5);border-radius:50%;
           display:flex;align-items:center;justify-content:center;
           font-size:1.4rem;font-weight:900;color:white;margin-bottom:.75rem;}
    .cp-nm{font-size:1.25rem;font-weight:900;color:white;margin-bottom:.15rem;}
    .cp-ph2{font-size:.82rem;color:rgba(255,255,255,.88);display:flex;align-items:center;gap:.3rem;}
    .cp-tabs{display:flex;background:rgba(255,255,255,.15);border-radius:9999px;
             padding:.2rem;margin-top:1rem;gap:.2rem;}
    .cp-tab{flex:1;padding:.4rem;border:none;border-radius:9999px;font-weight:700;
            font-size:.82rem;cursor:pointer;transition:all .25s;background:transparent;
            color:rgba(255,255,255,.8);font-family:'Nunito',sans-serif;}
    .cp-tab.act{background:white;color:#059669;}
    .cp-body{padding:1.25rem;}
    .cp-card{background:white;border-radius:1.1rem;border:1px solid #e5e7eb;
             margin-bottom:1rem;overflow:hidden;}
    .cp-card-hd{display:flex;align-items:center;justify-content:space-between;
                padding:.8rem 1.1rem;border-bottom:1px solid #f3f4f6;}
    .cp-card-ttl{font-size:.85rem;font-weight:800;color:#374151;
                 display:flex;align-items:center;gap:.4rem;}
    .cp-card-ttl svg{width:.95rem;height:.95rem;color:#059669;}
    .cp-edit-btn{display:flex;align-items:center;gap:.25rem;background:#f0fdf4;color:#059669;
                 border:1.5px solid #a7f3d0;border-radius:9999px;padding:.25rem .65rem;
                 font-size:.72rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .cp-row{display:flex;align-items:flex-start;gap:.65rem;padding:.7rem 1.1rem;
            border-bottom:1px solid #f9fafb;}
    .cp-row:last-child{border-bottom:none;}
    .cp-row-ic{width:1.85rem;height:1.85rem;background:#ecfdf5;border-radius:.5rem;
               flex-shrink:0;display:flex;align-items:center;justify-content:center;}
    .cp-row-ic svg{width:.85rem;height:.85rem;color:#059669;}
    .cp-row-lbl{font-size:.68rem;font-weight:700;color:#9ca3af;
                text-transform:uppercase;letter-spacing:.4px;margin-bottom:.1rem;}
    .cp-row-val{font-size:.88rem;font-weight:700;color:#111827;}
    .cp-row-val.empty{color:#d1d5db;font-weight:400;font-style:italic;font-size:.82rem;}
    .cp-form-field{margin-bottom:.8rem;}
    .cp-form-lbl{display:block;font-size:.75rem;font-weight:700;color:#374151;margin-bottom:.3rem;}
    .cp-form-inp{width:100%;padding:.62rem .9rem;border:2px solid #e5e7eb;border-radius:.75rem;
                 font-size:.88rem;font-family:'Nunito',sans-serif;font-weight:600;
                 transition:border-color .2s;outline:none;}
    .cp-form-inp:focus{border-color:#10b981;box-shadow:0 0 0 3px rgba(16,185,129,.1);}
    .cp-save-btn{background:linear-gradient(135deg,#10b981,#059669);color:white;border:none;
                 border-radius:9999px;padding:.65rem 1.4rem;font-weight:800;font-size:.88rem;
                 cursor:pointer;font-family:'Nunito',sans-serif;display:inline-flex;
                 align-items:center;gap:.4rem;}
    .cp-cancel-btn{background:#f3f4f6;color:#374151;border:none;border-radius:9999px;
                   padding:.65rem 1.1rem;font-weight:700;font-size:.88rem;cursor:pointer;
                   font-family:'Nunito',sans-serif;margin-left:.4rem;}
    .cp-ord{display:flex;align-items:center;justify-content:space-between;gap:.75rem;
            padding:.8rem 1.1rem;border-bottom:1px solid #f9fafb;}
    .cp-ord:last-child{border-bottom:none;}
    .cp-ord-num{font-size:.78rem;font-weight:800;color:#111827;}
    .cp-ord-date{font-size:.66rem;color:#9ca3af;margin-top:.1rem;}
    .cp-ord-amt{font-size:.92rem;font-weight:900;color:#059669;white-space:nowrap;}
    .cp-ord-badge{font-size:.65rem;font-weight:700;padding:.18rem .6rem;border-radius:9999px;white-space:nowrap;}
    .cp-empty{text-align:center;padding:2rem 1rem;color:#9ca3af;font-size:.88rem;}
    .cp-logout{width:100%;background:white;color:#ef4444;border:2px solid #fee2e2;
               border-radius:9999px;padding:.75rem;font-weight:700;font-size:.92rem;cursor:pointer;
               transition:all .3s;display:flex;align-items:center;justify-content:center;
               gap:.5rem;font-family:'Nunito',sans-serif;margin-top:.5rem;}
    .cp-logout:hover{background:#fff1f2;}
  `;

  return (
    <>
      <style>{css}</style>
      <div className="cp-ov" onClick={onClose||undefined}>
        <div className="cp-bx" onClick={e=>e.stopPropagation()}>
          {/* Header */}
          <div className="cp-hdr">
            {onClose&&<button className="cp-cl" onClick={onClose}><XCircleIcon style={{width:'1rem',height:'1rem'}}/></button>}
            <div className="cp-av">{(profile.name||'U').charAt(0).toUpperCase()}</div>
            <div className="cp-nm">{profile.name||'Fresh Hills Customer'}</div>
            <div className="cp-ph2"><DevicePhoneMobileIcon style={{width:'.88rem',height:'.88rem'}}/>+{profile.phone}</div>
            <div className="cp-tabs">
              <button className={`cp-tab${tab==='profile'?' act':''}`} onClick={()=>setTab('profile')}>👤 Profile</button>
              <button className={`cp-tab${tab==='orders'?' act':''}`} onClick={()=>setTab('orders')}>📦 Orders ({orders.length})</button>
            </div>
          </div>

          <div className="cp-body">
            {/* ── Profile Tab ── */}
            {tab==='profile'&&(
              <div className="cp-card">
                <div className="cp-card-hd">
                  <span className="cp-card-ttl"><UserIcon/>My Details</span>
                  {!editing&&<button className="cp-edit-btn" onClick={()=>setEditing(true)}><PencilIcon style={{width:'.7rem',height:'.7rem'}}/>Edit</button>}
                </div>

                {editing?(
                  <form onSubmit={handleSave} style={{padding:'1rem 1.1rem'}}>
                    <div className="cp-form-field">
                      <label className="cp-form-lbl">Full Name</label>
                      <input className="cp-form-inp" type="text" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Your full name"/>
                    </div>
                    <div className="cp-form-field">
                      <label className="cp-form-lbl">Email (optional)</label>
                      <input className="cp-form-inp" type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="your@email.com"/>
                    </div>
                    <div className="cp-form-field">
                      <label className="cp-form-lbl">Delivery Address</label>
                      <input className="cp-form-inp" type="text" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} placeholder="House no, Street, City"/>
                    </div>
                    <button className="cp-save-btn" type="submit" disabled={saving}>
                      {saving?<><Spin/>Saving…</>:'Save Changes ✓'}
                    </button>
                    <button type="button" className="cp-cancel-btn" onClick={()=>setEditing(false)}>Cancel</button>
                  </form>
                ):(
                  <>
                    <div className="cp-row">
                      <div className="cp-row-ic"><UserIcon/></div>
                      <div><div className="cp-row-lbl">Name</div><div className={`cp-row-val${!profile.name?' empty':''}`}>{profile.name||'Not set — tap Edit'}</div></div>
                    </div>
                    <div className="cp-row">
                      <div className="cp-row-ic"><DevicePhoneMobileIcon/></div>
                      <div><div className="cp-row-lbl">WhatsApp</div><div className="cp-row-val">+{profile.phone}</div></div>
                    </div>
                    <div className="cp-row">
                      <div className="cp-row-ic"><EnvelopeIcon/></div>
                      <div><div className="cp-row-lbl">Email</div><div className={`cp-row-val${!profile.email?' empty':''}`}>{profile.email||'Not set'}</div></div>
                    </div>
                    <div className="cp-row">
                      <div className="cp-row-ic"><MapPinIcon/></div>
                      <div><div className="cp-row-lbl">Address</div><div className={`cp-row-val${!profile.address?' empty':''}`}>{profile.address||'Not set'}</div></div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Orders Tab ── */}
            {tab==='orders'&&(
              <div className="cp-card">
                <div className="cp-card-hd">
                  <span className="cp-card-ttl"><ShoppingBagIcon/>Order History</span>
                </div>
                {orders.length===0
                  ?<div className="cp-empty">📦 No orders yet.<br/>Start shopping to see your orders here!</div>
                  :orders.map(o=>{
                    const st=STATUS[o.status]||STATUS.pending;
                    return(
                      <div key={o.order_number} className="cp-ord">
                        <div style={{flex:1,minWidth:0}}>
                          <div className="cp-ord-num">#{o.order_number}</div>
                          <div className="cp-ord-date">{new Date(o.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                        </div>
                        <span className="cp-ord-badge" style={{background:st.bg,color:st.color}}>{st.label}</span>
                        <div className="cp-ord-amt">₹{Number(o.total).toFixed(2)}</div>
                      </div>
                    );
                  })}
              </div>
            )}

            <button className="cp-logout" onClick={handleLogout}>
              <ArrowRightOnRectangleIcon style={{width:'1.1rem',height:'1.1rem'}}/>Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerProfile;